"use client"

import * as React from "react"
import { Tag as TagIcon, Plus, X, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { MAX_TAGS_PER_LINK } from "@/features/recent-links/constants"
import { Tag } from "@/features/recent-links/types"
import { toast } from "sonner"

export interface TagSelectorProps {
  // Data
  tags: Tag[]
  selectedTags: Tag[]
  
  // Callbacks
  onTagSelect: (tag: Tag) => void
  onTagRemove: (tagId: number) => void
  onTagCreate?: (name: string) => Promise<void>
  onOpenChange?: (isOpen: boolean) => void
  
  // Configuration
  variant?: 'default' | 'compact' | 'inline'
  placeholder?: string
  maxTags?: number
  allowCreate?: boolean
  disabled?: boolean
  
  // Styling
  className?: string
}

export function TagSelector({
  tags,
  selectedTags,
  onTagSelect,
  onTagRemove,
  onTagCreate,
  onOpenChange,
  variant = 'default',
  placeholder = "Search or create tag...",
  maxTags = MAX_TAGS_PER_LINK,
  allowCreate = true,
  disabled = false,
  className
}: TagSelectorProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const [isCreating, setIsCreating] = React.useState(false)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  const canAddMoreTags = selectedTags.length < maxTags

  // Handle click outside to close dropdown
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        onOpenChange?.(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Filter available tags
  const availableTags = React.useMemo(() => {
    if (!Array.isArray(tags)) return []
    
    const searchLower = search.toLowerCase().trim()
    return tags.filter(tag => {
      const isNotSelected = !selectedTags.some(selected => selected.id === tag.id)
      const matchesSearch = searchLower === "" || tag.name.toLowerCase().includes(searchLower)
      return isNotSelected && matchesSearch
    })
  }, [tags, selectedTags, search])

  // Check if we can create a new tag
  const showCreateOption = React.useMemo(() => {
    if (!allowCreate || !onTagCreate) return false
    const searchTrimmed = search.trim()
    if (!searchTrimmed || !canAddMoreTags) return false
    
    return !tags.some(tag => 
      tag.name.toLowerCase() === searchTrimmed.toLowerCase()
    )
  }, [search, tags, canAddMoreTags, allowCreate, onTagCreate])

  const handleCreateTag = async () => {
    if (!onTagCreate) return
    const searchTrimmed = search.trim()
    if (!searchTrimmed) return

    try {
      setIsCreating(true)
      await onTagCreate(searchTrimmed)
      setSearch("")
      setIsOpen(false)
    } catch (error) {
      toast.error("Failed to create tag")
    } finally {
      setIsCreating(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && showCreateOption) {
      e.preventDefault()
      handleCreateTag()
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      onOpenChange?.(false)
    }
  }

  // Variant-specific styling
  const getVariantStyles = () => {
    switch (variant) {
      case 'compact':
        return {
          container: "flex flex-wrap items-center gap-1",
          badge: "text-xs px-1.5 py-0.5",
          button: "text-xs px-1.5 py-0.5",
          dropdown: "w-[180px]"
        }
      case 'inline':
        return {
          container: "flex flex-wrap items-center gap-1.5",
          badge: "text-xs px-2 py-0.5",
          button: "text-xs px-2 py-0.5",
          dropdown: "w-[200px]"
        }
      default:
        return {
          container: "flex flex-wrap items-center gap-2",
          badge: "text-sm px-2 py-1",
          button: "text-sm px-2 py-1",
          dropdown: "w-[240px]"
        }
    }
  }

  const styles = getVariantStyles()

  return (
    <div className={cn(styles.container, "w-full", className)}>
      {/* Selected tags */}
      {selectedTags.map((tag) => (
        <Badge
          key={`selected-${tag.id}`}
          variant="secondary"
          className={cn(
            "flex items-center gap-1 font-medium",
            "bg-gray-100/80 hover:bg-gray-200/80 dark:bg-gray-800/80 dark:hover:bg-gray-700/80",
            "text-gray-800 dark:text-gray-200",
            "border border-gray-200/50 dark:border-gray-700/50",
            "transition-colors duration-200",
            styles.badge
          )}
        >
          <TagIcon className="h-3 w-3 text-blue-500/80 dark:text-blue-400/80" />
          {tag.name}
          <button
            className="rounded-full hover:bg-gray-300/50 dark:hover:bg-gray-600/50 p-0.5 transition-colors"
            onClick={() => onTagRemove(tag.id)}
            disabled={disabled}
            type="button"
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Remove {tag.name} tag</span>
          </button>
        </Badge>
      ))}

      {/* Add tag button and dropdown */}
      <div className="relative" ref={dropdownRef}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            const newOpenState = !isOpen
            setIsOpen(newOpenState)
            onOpenChange?.(newOpenState)
          }}
          disabled={!canAddMoreTags || disabled}
          className={cn(
            "flex items-center gap-1.5 border-dashed",
            "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200",
            "hover:bg-gray-50 dark:hover:bg-gray-800/50",
            styles.button,
            (disabled || !canAddMoreTags) && "opacity-50 cursor-not-allowed"
          )}
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Add tag</span>
        </Button>

        {isOpen && (
          <div className={cn(
            "absolute left-0 top-full mt-1 z-50",
            "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg",
            styles.dropdown
          )}>
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  className="pl-8 h-8 text-sm"
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-[200px] overflow-y-auto py-1">
              {showCreateOption && (
                <button
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  onClick={handleCreateTag}
                  disabled={isCreating}
                  type="button"
                >
                  <Plus className="h-3.5 w-3.5 text-green-500" />
                  <span>Create "<span className="font-medium">{search.trim()}</span>"</span>
                </button>
              )}

              {availableTags.map((tag) => (
                <button
                  key={tag.id}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                  onClick={() => {
                    onTagSelect(tag)
                    setSearch("")
                    setIsOpen(false)
                    onOpenChange?.(false)
                  }}
                  type="button"
                >
                  <TagIcon className="h-3.5 w-3.5 text-blue-500" />
                  {tag.name}
                </button>
              ))}

              {availableTags.length === 0 && !showCreateOption && (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
                  No tags found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 